import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ContainerValue, DialogWrapper, FlexBox } from "components/containers";
import {
  Button,
  TextLink,
  TextPlain,
  TitleH18,
  TitleH2,
  TitleH3,
  TitleH4,
} from "components/generic";
import Modal from "components/Modal";
import { DateLine, SpokenLanguages } from "pages/ProfileUser/SimpleComponents";

import { WrapperContainer } from "./styles";
import {
  DateNone,
  IHostStripeMatches,
  IMatchesEnum,
  TModeMatch,
} from "interfaces/intarfaces";
import { getNameCountry } from "utils/fetchMapInfo";
import { HOST_CHATS_ROUTE } from "utils/constants";
import Calendar, { useCalendar } from "components/Calendar";
import { getPeriod, isoDate, getDateOrNone } from "utils/helpers";
import { StudentAboutInfo } from "pages/ProfileUser/StudentAboutInfo";

import { TLang } from "assets/data/isoNameLanguage";

export const MatchCard: React.FC<
  IHostStripeMatches & {
    handleMatchStatus: (
      match_data: Pick<IMatchesEnum, "house_id" | "student_id">,
      mode: TModeMatch,
      interval?: [string, string] | undefined
    ) => void;
  }
> = ({ student, experience, house, handleMatchStatus }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("matches");

  const [showModal, setShowModal] = useState<number>(0);
  const [error, setError] = useState<string>('')

  const valuePrev = useRef<DateNone>(undefined);
  const endIntervalPrev = useRef<DateNone>(undefined);


  const moveInDate = useCalendar({
    value: getDateOrNone(student.preferred_move_in),
  });
  const moveOutDate = useCalendar({
    value: getDateOrNone(student.preferred_move_out),
  });

  const dateRange = useCalendar({
    ...getPeriod(),
    range: true,
  });

  const name = `${student.first_name} ${student.last_name}`;
  const age =
    new Date().getFullYear() -
    new Date(student.birth_date ?? new Date()).getFullYear();

  const handleMatch = (mode: TModeMatch) => {
    if (!error) {
      handleMatchStatus(
        {
          student_id: student.user_id,
          house_id: house.house_id!,
        },
        mode,
        mode === "acceptInvite"
          ? [isoDate(dateRange.value), isoDate(dateRange.endInterval)]
          : undefined
      );
      setShowModal(0);
    }
  };

  useEffect(() => {
    valuePrev.current = dateRange.value;
    endIntervalPrev.current = dateRange.endInterval;
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!dateRange.value || !dateRange.endInterval) {
      setError(t("host.startAndEnd"))
    } else if (dateRange.value?.toISOString().slice(0,-7) === dateRange.endInterval?.toISOString().slice(0,-7)) {
      setError(t("host.sameDate"))
    } else {
      setError('')
    }
  }, [dateRange.endInterval, dateRange.value]);

  const backToOldDate = () => {
    dateRange.onChange(valuePrev.current, endIntervalPrev.current);
    setShowModal(0);
  };

  const currLang = i18n.language.toUpperCase() as TLang;

  return (
    <ContainerValue style={{ margin: 0 }}>
      <WrapperContainer>
        <FlexBox justify="space-between">
          <TitleH18>
            {t("host.card.respondedTo")}: {house.title}
          </TitleH18>
          <TextLink
            onClick={() => setShowModal(3)}
            style={{ margin: "0 0 0 auto", fontSize: "16px" }}
          >
            {t("host.card.buttons.removematch")}
          </TextLink>
        </FlexBox>

        <div className="main">
          <img src={student.thumbnail_url + `?${new Date().toString()}`} alt="Profile" />
          <div className="info">
            <TitleH2 m="0 0 1em 0">{name}</TitleH2>
            <TitleH4>{t(`host.userInfo.gender.${student.gender}`)}</TitleH4>
            <TitleH4>{`${age} ${t("host.card.yearOld")}`}</TitleH4>
            <TitleH4>{getNameCountry(student.birth_country!, currLang)}</TitleH4>
            {student.spoken_languages &&
              student.spoken_languages.length > 0 && (
                <SpokenLanguages items={student.spoken_languages || ""} />
              )}
            <TitleH3 style={{ marginTop: "8px" }}>
              {`${t("host.card.aboutPerson")} ${name}`}
            </TitleH3>
            <TitleH4 className="aboutStudent">{student.description}</TitleH4>
            <TextLink onClick={() => setShowModal(1)}>
              {t("host.card.readMore")}
            </TextLink>
          </div>
        </div>
        <hr />
        <div className="footer">
          <div className="dates">
            <DateLine
              start={isoDate(moveInDate.value)}
              end={isoDate(moveOutDate.value)}
              ico
            />
          </div>
          <div className="buttons">
            <Button
              normal
              onClick={() =>
                  navigate(
                  `/${i18n.language}${HOST_CHATS_ROUTE.slice(5)}` + `/${student.user_id}/${house.house_id}`
                )
              }
            >
              {t("host.card.buttons.chat")}
            </Button>
            {experience.isInvited ? (
              <Button
                outlineSpecial
                onClick={() => handleMatch("rejectInvite")}
              >
                {t("host.card.buttons.decline")}
              </Button>
            ) : (
              <Button
                main
                onClick={() => setShowModal(experience.isChatted ? 2 : 4)}
              >
                {t("host.card.buttons.invite")}
              </Button>
            )}
          </div>
        </div>
      </WrapperContainer>
      {!!showModal && (
        <Modal isShow={!!showModal} handleClose={() => setShowModal(0)}>
          {showModal === 1 && (
            <DialogWrapper>
              <StudentAboutInfo {...student} closeBtn={setShowModal} />
            </DialogWrapper>
          )}
          {showModal === 2 && (
            <DialogWrapper>
              <TitleH2>{t("host.dateRange.title")}</TitleH2>
              <div>
                <TitleH4>{t("host.dateRange.text")}</TitleH4>
                <div className="cal">
                  <Calendar {...dateRange} error={error} />
                  <div style={{ textAlign: 'center', marginTop: '2px', color: 'red', wordBreak: 'break-word' }}>
                    {error}
                  </div>
                </div>
              </div>
              <div className="buttons">
                <Button normal onClick={() => backToOldDate()}>
                  {t("host.dateRange.cancel")}
                </Button>
                <Button main onClick={() => handleMatch("acceptInvite")}>
                  {t("host.dateRange.buttonPropose")}
                </Button>
              </div>
            </DialogWrapper>
          )}
          {showModal === 3 && (
            <DialogWrapper>
              <TitleH2>{t(`host.removeMatch.title`)}</TitleH2>
              <TextPlain
                style={{ whiteSpace: "pre-line", wordBreak: "break-word" }}
              >
                {t(`host.removeMatch.text`, {
                  joinArrays: "\n",
                })}
              </TextPlain>
              <div className="buttons">
                <Button normal onClick={() => setShowModal(0)}>
                  {t(`host.removeMatch.buttonCancel`)}
                </Button>
                <Button danger onClick={() => handleMatch("removeMatch")}>
                  {t(`host.removeMatch.buttonRemove`)}
                </Button>
              </div>
            </DialogWrapper>
          )}
          {showModal === 4 && (
            <DialogWrapper style={{gap: 0}}>
              <TitleH2>{t(`host.card.chatFirst`)}</TitleH2>
              <div className="buttons" style={{marginTop: 20}}>
                <Button normal onClick={() => setShowModal(0)}>
                  {t(`host.removeMatch.buttonCancel`)}
                </Button>
                <Button
                    main
                    onClick={() =>
                        navigate(
                            `/${i18n.language}${HOST_CHATS_ROUTE.slice(5)}` + `/${student.user_id}/${house.house_id}`
                        )
                    }
                >
                  {t("host.card.buttons.chat")}
                </Button>
              </div>
            </DialogWrapper>
          )}
        </Modal>
      )}
    </ContainerValue>
  );
};
